<template>
  <div>
    <VCard
      flat
      class="card-actions d-flex flex-column justify-end grey lighten-4 fill-height"
    >
      <VForm>
        <div class="card-actions__title">
          {{ $t('chooseTitle') }}:
        </div>
        <div class="mb-3">
          <div
            class="card-actions__item"
            v-for="availableType in availableTypes"
            :key="availableType.type"
            :value="availableType.type"
            :class="{selected: chosenType === availableType.type, disabled: false}"
            @click="chosenType = availableType.type"
          >
            <div class="card-actions__item-title">
              {{ $t(`${availableType.type}.title`) }}
            </div>
            {{ $t(`${availableType.type}.description`) }}
          </div>
        </div>
        <VBtn
          :disabled="!chosenType"
          @click="startInterview"
          class="mr-3"
        >
          {{ $t('next') }}
        </VBtn>
        <VBtn
          v-if="hasInterviews"
          @click="cancel"
        >
          {{ $t('cancel') }}
        </VBtn>
      </VForm>
    </VCard>
  </div>
</template>

<script>
import { candidateInterviewType } from '@/enums';
import useCandidates from '@/compositions/useCandidates';

export default {
  name: 'CreateInterviewPage',
  props: {
    candidate: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const {
      checkIfAlreadyHasOfficeInterview,
      checkIfAlreadyHasOnlineInterview,
    } = useCandidates();

    return {
      checkIfAlreadyHasOfficeInterview,
      checkIfAlreadyHasOnlineInterview,
    };
  },
  data() {
    return {
      chosenType: null,
    };
  },
  computed: {
    availableTypes() {
      const availableTypes = [];

      availableTypes.push({
        type: candidateInterviewType.ONLINE_INTERVIEW,
        disabled: !this.candidate.canCreateOnlineInterview,
      });

      availableTypes.push({
        type: candidateInterviewType.VIDEO_CALL,
        disabled: !this.candidate.canCreateVideoCall,
      });

      availableTypes.push({
        type: candidateInterviewType.OFFICE_INTERVIEW,
        disabled: !this.candidate.canCreateOfficeInterview,
      });

      availableTypes.push({
        type: candidateInterviewType.UPLOADED_VIDEO,
        disabled: false,
      });

      return availableTypes;
    },
    hasInterviews() {
      return !!this.candidate.officeInterviews?.length > 0
        || !this.candidate.canCreateOnlineInterview;
    },
  },
  methods: {
    async startInterview() {
      const hasInterview = await this.checkIfAlreadyHasOfficeInterview(
        this.candidate,
        this.chosenType,
      );

      switch (this.chosenType) {
        case candidateInterviewType.ONLINE_INTERVIEW: {
          const hasOnlineInterview = await this.checkIfAlreadyHasOnlineInterview(this.candidate);

          if (hasOnlineInterview) {
            this.cancel();
            this.$emit('show-last', this.chosenType);
            break;
          }

          this.cancel();
          this.$emit('online-interview');
          break;
        }
        case candidateInterviewType.VIDEO_CALL: {
          if (hasInterview) {
            this.cancel();
            this.$emit('show-last', this.chosenType);
            break;
          }

          this.cancel();
          this.$emit('video-call');
          break;
        }
        case candidateInterviewType.OFFICE_INTERVIEW: {
          if (hasInterview) {
            this.cancel();
            this.$emit('show-last', this.chosenType);
            break;
          }

          this.cancel();
          this.$emit('office-interview');
          break;
        }
        case candidateInterviewType.UPLOADED_VIDEO: {
          this.$emit('uploaded-video');
          break;
        }
        default: {
          break;
        }
      }
    },
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
  .card-actions {
    padding: 1.375rem 3.125rem 1.5rem;
    border-radius: 0.5rem;
    &__title {
      font-size: 1.125rem;
      line-height: 1.11;
      color: #212121;
      margin-bottom: 0.875rem;
    }
    &__item {
      padding: 0.5625rem 1.25rem;
      background: #FFFFFF;
      border: 1px solid #E0E0E0;
      border-radius: 0.25rem;
      font-weight: 300;
      font-size: 0.75rem;
      line-height: 1.17;
      color: #757575;
      margin-bottom: 0.5rem;
      cursor: pointer;
      -moz-user-select: none;
      -ms-user-select: none;
      -o-user-select: none;
      -webkit-user-select: none;
      user-select: none;
      &.selected {
        border-color: #0A82D9;
        color: #0A82D9;
        .card-actions__item-title {
          color: #0A82D9;
        }
      }
      &.disabled {
         pointer-events: none;
        background: transparent;
      }
    }
    &__item-title {
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.3;
      color: #212121;
    }
  }
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .card-actions {
      padding: 1.75rem 2rem;
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .card-actions {
      padding: 1.375rem 1rem;
      &__title {
        margin-bottom: 0.75rem;
      }
    }
  }
</style>

<i18n locale="en" lang="yaml">
chooseTitle: Choose an action
online_interview:
  title: Invite to a virtual interview
  description: The interview is conducted by our actor based on your script
video_call:
  title: Create videocall
  description: A live interview like in Zoom. The recording will be uploaded here
office_interview:
  title: Record an in-office meeting
  description: This option is handy when your colleagues cannot be present at the interview
uploaded_video:
  title: Load video file
  description: Add the recording of an interview conducted via Zoom or Skype
next: Next
cancel: Cancel
</i18n>

<i18n locale="ru" lang="yaml">
chooseTitle: Выберите действие
online_interview:
  title: Пригласить на автоматическое интервью
  description: интервью проведет наш актер по вашему сценарию
video_call:
  title: Создать видеозвонок
  description: все как в Zoom, запись появится в карточке кандидата
office_interview:
  title: Записать встречу в офисе
  description: пригодится, если кто-то из коллег не может присутствовать
uploaded_video:
  title: Загрузить файл с видео
  description: добавьте в карточку кандидата запись встречи из Zoom или Skype
next: Далее
cancel: Отмена
</i18n>
